/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { Alert } from 'antd'
import CreditCardInput from '../../components/CreditCardInput'
import Icon from '../../components/Icon'
import stripeLogo from './stripe_logo.svg'
import PaymentError from './components/PaymentError'

import { useI18n } from '@shopify/react-i18n'
import { ROUTES } from '@sportninja/common/constants/app'
import { useCountries } from '@sportninja/common/hooks/useCountries'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { PAYMENT_PROVIDERS } from 'src/hooks/usePaymentProvider'
import useSinglePaymentProvider from 'src/hooks/useSinglePaymentProvider'
import LoadingSpinner from '../../components/LoadingSpinner'

const PAYMENT_STATUS = {
  SUCCESS: 'success',
  FAILED: 'failed',
}

const RegistrationPayment = () => {
  const history = useHistory()
  const [i18n] = useI18n()

  const { search } = useLocation()

  // State for credit card input values (for SportsPay)
  const [creditCardValues, setCreditCardValues] = useState({
    cardNumber: '',
    expiryDate: '',
    cvc: '',
    country: '',
    cardType: '',
    errors: {},
  })

  const params = useMemo(() => {
    // Full-balance ("Pay Full Balance") mode: ?teamRegistrationId=...
    const payInFull = /[?&]teamRegistrationId=([^&]+)/.exec(search)
    if (payInFull) {
      return { teamRegistrationId: payInFull[1] }
    }

    // Prefer new format: ?billingId=...
    const simple = /\?billingId=([^&]+)/.exec(search)
    if (simple) {
      return { billingId: simple[1] }
    }

    // Fallback to legacy formats for backward compatibility
    const divisionRegex =
      /\?divisionId=([^&]+)&teamId=([^&]+)&billingId=([^&]+)/
    const divisionMatch = divisionRegex.exec(search)
    if (divisionMatch) {
      return { billingId: divisionMatch[3] }
    }

    const billingRegex = /\?teamId=([^&]+)&rosterId=([^&]+)&billingId=([^&]+)/
    const billingMatch = billingRegex.exec(search)
    if (billingMatch) {
      return { billingId: billingMatch[3] }
    }
  }, [search])

  const {
    billingSchedule,
    error,
    paymentIsLoading,
    paymentStatus,
    paymentError,
    bootstrapFinished,
    retryBootstrap,
    totalTaxSum,
    paymentProvider,
    processSportsPayPayment,
  } = useSinglePaymentProvider({
    billingId: params?.billingId,
    teamRegistrationId: params?.teamRegistrationId,
  })

  const billing = billingSchedule

  // Consolidated 'full' rows are a stored contract: display the stamped tax and
  // total — exactly what the server charges — instead of recomputing current rates
  const isFullPaymentRow = billing?.payment_type === 'full'
  const paymentTypeLabel = isFullPaymentRow
    ? 'Full Payment'
    : billing?.payment_type

  const { countries, refetch: refetchCountries } = useCountries()
  const [localError, setLocalError] = useState(null)

  // Combine all error sources for display
  const displayError =
    localError ||
    paymentError ||
    (paymentStatus === PAYMENT_STATUS.FAILED && error)

  useEffect(() => {
    if (countries && countries.length === 0) {
      refetchCountries()
    }
  }, [countries, refetchCountries])

  // Handle credit card input changes
  const handleCreditCardChange = (values) => {
    setCreditCardValues(values)
  }

  // Format credit card data for SportsPay
  const formatSportsPayCardData = useCallback(() => {
    if (!creditCardValues) {
      return null
    }

    // Extract month and year from expiry date (MM / YY format)
    const expiryParts = creditCardValues.expiryDate.split(' / ')
    const month = expiryParts[0]
    const year = expiryParts[1] ? `20${expiryParts[1]}` : ''

    return {
      CARDNUM: creditCardValues.cardNumber.replace(/\s+/g, ''),
      CARDEXPMM: month,
      CARDEXPYYYY: year,
      CARDCVV: creditCardValues.cvc,
    }
  }, [creditCardValues])

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    if (paymentProvider === PAYMENT_PROVIDERS.SPORTS_PAY) {
      // Check for credit card validation errors
      if (creditCardValues.errors?.expiryDate) {
        setLocalError(creditCardValues.errors.expiryDate)
        return
      }

      // Format card data for SportsPay
      const cardData = formatSportsPayCardData()
      if (!cardData) {
        setLocalError('Please enter valid card details')
        return
      }

      // SportsPay payment processing
      await processSportsPayPayment(cardData)
    }
  }

  if (!params) {
    return (
      <PaymentError
        title='Payment Error'
        message={'Billing Not Found'}
        onPrimaryAction={() => history.replace(ROUTES.HOME)}
        buttonLabel='Back to Home'
      />
    )
  }

  if (error) {
    return (
      <PaymentError
        title='Payment Error'
        message={
          getErrorMessage(error) || error?.message || 'Something went wrong'
        }
        onPrimaryAction={() => history.replace(ROUTES.HOME)}
        buttonLabel='Back to Home'
      />
    )
  }

  if (localError) {
    return (
      <PaymentError
        title='Payment Intent Error'
        message={
          getErrorMessage(localError) ||
          (typeof localError === 'string' ? localError : localError?.message) ||
          'Something went wrong'
        }
        onPrimaryAction={() => history.replace(ROUTES.HOME)}
        buttonLabel='Back to Home'
      />
    )
  }

  const orgImageUrl = resolveAssetUrl(billing?.organization_image)
  const rootScheduleImageUrl = resolveAssetUrl(billing?.root_schedule_image)

  return (
    <div
      css={css`
        display: flex;
        height: 100vh;

        @media (max-width: 768px) {
          flex-direction: column;
          height: 100%;
        }
      `}
    >
      <div
        css={css`
          width: 50%;
          padding-top: 80px;
          padding-left: 106px;
          padding-right: 140px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;

          @media (max-width: 768px) {
            width: 100%;
            padding-left: 20px;
            padding-right: 20px;
          }
        `}
      >
        <div
          onClick={() => history.replace(ROUTES.HOME)}
          css={css`
            display: flex;
            align-items: center;

            &:hover {
              cursor: pointer;
            }
          `}
        >
          <Icon name='home' color={colors.DEFAULT_FLAIR} fontSize={12} />
          <p
            css={css`
              font-size: 12px;
              font-weight: 500;
              margin-left: 12px;
              color: ${colors.DEFAULT_FLAIR};
            `}
          >
            GO HOME
          </p>
        </div>

        <div>
          {billing?.subtotal ? (
            <>
              {orgImageUrl && (
                <img
                  src={orgImageUrl}
                  alt={billing?.organization_name + ' League Logo'}
                  css={css`
                    margin-top: 32px;
                    width: 160px;
                    height: auto;
                    max-height: 160px;
                  `}
                />
              )}
              <div
                css={css`
                  margin-top: 56px;
                `}
              >
                <p
                  css={css`
                    font-weight: 700;
                    font-size: 18px;
                  `}
                >
                  Paying {billing?.single_payment ? '' : paymentTypeLabel}
                  {' to '}
                  {billing?.organization_name
                    ? billing?.organization_name
                    : 'SportNinja'}
                </p>
                <p
                  css={css`
                    font-weight: 700;
                    font-size: 40px;
                    margin-top: 14px;
                  `}
                >
                  {i18n.formatCurrency(
                    // Full rows charge the stored tax-inclusive total — the
                    // headline amount must match what the card is charged
                    isFullPaymentRow ? billing?.total : billing?.subtotal,
                    {
                      currency: billing?.currency_code?.toUpperCase(),
                      form: 'short',
                    }
                  )}
                </p>
              </div>
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  margin-top: 56px;
                `}
              >
                {rootScheduleImageUrl && (
                  <img
                    src={rootScheduleImageUrl}
                    css={css`
                      width: 50px;
                      height: auto;
                      margin-right: 16px;
                    `}
                  />
                )}
                <p
                  css={css`
                    font-size: 16px;
                    font-weight: 700;
                  `}
                >
                  {`${billing?.root_schedule_name} - ${billing?.division_name}`}
                </p>
              </div>
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  margin-top: 56px;
                  padding-bottom: 8px;
                `}
              >
                <p
                  css={css`
                    font-size: 16px;
                    text-transform: capitalize;
                    text-align: right;
                    flex: 1;

                    @media (max-width: 768px) {
                      text-align: left;
                    }
                  `}
                >
                  {paymentTypeLabel || 'Subtotal'}
                </p>
                <p
                  css={css`
                    font-size: 16px;
                    flex: 1;
                    text-align: right;
                  `}
                >
                  {i18n.formatCurrency(billing?.subtotal, {
                    currency: billing?.currency_code?.toUpperCase(),
                    form: 'explicit',
                  })}
                </p>
              </div>
              {isFullPaymentRow && Number(billing?.taxtotal) > 0 ? (
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-top: 8px;
                  `}
                >
                  <p
                    css={css`
                      font-size: 16px;
                      text-align: right;
                      flex: 1;
                      align-items: center;

                      @media (max-width: 768px) {
                        text-align: left;
                      }
                    `}
                  >
                    Tax
                  </p>
                  <p
                    css={css`
                      font-size: 16px;
                      flex: 1;
                      text-align: right;
                    `}
                  >
                    {i18n.formatCurrency(billing?.taxtotal, {
                      currency: billing?.currency_code?.toUpperCase(),
                      form: 'explicit',
                    })}
                  </p>
                </div>
              ) : null}
              {!isFullPaymentRow &&
                billing?.taxes.map((tax) => (
                  <div
                    key={tax?.uid}
                    css={css`
                      display: flex;
                      align-items: center;
                      justify-content: space-between;
                      margin-top: 8px;
                    `}
                  >
                    <p
                      css={css`
                        font-size: 16px;
                        text-transform: capitalize;
                        text-align: right;
                        flex: 1;
                        align-items: center;

                        @media (max-width: 768px) {
                          text-align: left;
                        }
                      `}
                    >
                      {tax?.name} - {tax?.description} (
                      {Number(tax?.tax_rate).toFixed(1)}%)
                    </p>
                    <p
                      css={css`
                        font-size: 16px;
                        flex: 1;
                        text-align: right;
                      `}
                    >
                      {i18n.formatCurrency(
                        billing?.subtotal * (Number(tax?.tax_rate) / 100),
                        {
                          currency: billing?.currency_code?.toUpperCase(),
                          form: 'explicit',
                        }
                      )}
                    </p>
                  </div>
                ))}
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  margin-top: 16px;
                  padding-top: 16px;
                  border-top: 1px solid ${colors.SOFT_STEEL};
                `}
              >
                <p
                  css={css`
                    font-size: 16px;
                    font-weight: 700;
                    flex: 1;
                    text-align: right;

                    @media (max-width: 768px) {
                      text-align: left;
                    }
                  `}
                >
                  Total
                </p>
                <p
                  css={css`
                    font-size: 16px;
                    font-weight: 700;
                    flex: 1;
                    text-align: right;
                  `}
                >
                  {i18n.formatCurrency(
                    isFullPaymentRow
                      ? billing?.total
                      : billing?.subtotal +
                          billing?.subtotal * (totalTaxSum / 100),
                    {
                      currency: billing?.currency_code?.toUpperCase(),
                      form: 'explicit',
                    }
                  )}
                </p>
              </div>
            </>
          ) : (
            <LoadingSpinner />
          )}
        </div>
        <div
          css={css`
            padding-bottom: 106px;
            display: flex;
            align-items: center;

            @media (max-width: 768px) {
              display: none;
            }
          `}
        >
          <p>Powered by</p>
          {paymentProvider === PAYMENT_PROVIDERS.SPORTS_PAY && (
            <img
              src='/images/sportspay.png'
              css={css`
                margin-left: 6px;
                width: 32px;
                height: 32px;
              `}
            />
          )}
          {paymentProvider === PAYMENT_PROVIDERS.STRIPE && (
            <img
              src={stripeLogo}
              css={css`
                margin-left: 6px;
              `}
            />
          )}
        </div>
      </div>

      <div
        css={css`
          height: 100%;
          width: 1px;
          background-color: ${colors.SOFT_STEEL};
        `}
      />

      <div
        css={css`
          width: 50%;
          padding-top: 80px;
          padding-left: 106px;
          padding-right: 140px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;

          @media (max-width: 768px) {
            width: 100%;
            padding-left: 20px;
            padding-right: 20px;
            padding-top: 0px;
          }
        `}
      >
        <div
          css={css`
            margin-top: 32px;
            width: 100%;
          `}
        >
          {paymentStatus === PAYMENT_STATUS.SUCCESS ? (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
              `}
            >
              <Icon name='check' color={colors.DEFAULT_FLAIR} fontSize={48} />
              <p
                css={css`
                  margin-top: 32px;
                  font-size: 24px;
                  font-weight: 700;
                `}
              >
                Payment Successful
              </p>
              <button
                onClick={() => history.replace(ROUTES.HOME)}
                css={css`
                  margin-top: 100px;
                  display: flex;
                  flex-direction: row;
                  justify-content: center;
                  align-items: center;
                  padding: 8px 24px;
                  border: 2px solid ${colors.PRIMARY};
                  border-radius: 4px;
                  height: 48px;
                  width: 100%;
                  color: ${colors.WHITE};
                `}
              >
                GO BACK TO HOME
              </button>
            </div>
          ) : paymentStatus === PAYMENT_STATUS.FAILED ? (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
              `}
            >
              <Icon name='times' color={colors.ERROR} fontSize={48} />
              <p
                css={css`
                  margin-top: 32px;
                  font-size: 24px;
                  font-weight: 700;
                `}
              >
                Payment Failed
              </p>
              <span
                css={css`
                  margin-top: 20px;
                  text-transform: capitalize;
                `}
              >
                {displayError || 'Something Went Wrong'}
              </span>
              <button
                onClick={() => {
                  retryBootstrap()
                }}
                css={css`
                  height: 48px;
                  display: flex;
                  padding: 8px 24px;
                  justify-content: center;
                  align-items: center;
                  gap: 8px;
                  flex: 1 0 0;
                  border-radius: 8px;
                  cursor: ${paymentIsLoading ? 'not-allowed' : 'pointer'};
                  background-color: ${colors.PRIMARY};
                  color: ${colors.WHITE};
                  text-transform: uppercase;
                  margin-top: 16px;
                  width: 100%;
                  ${paymentIsLoading &&
                  css`
                    background-color: ${colors.SECONDARY_200};
                  `}
                `}
              >
                Try Again
              </button>
            </div>
          ) : (
            <div
              css={css`
                position: relative;
                min-height: 350px;
              `}
            >
              <div
                css={css`
                  visibility: ${bootstrapFinished ? 'visible' : 'hidden'};
                  contain: layout;
                `}
              >
                <form id='payment-form' onSubmit={handleSubmit}>
                  {/* Show our custom CreditCardInput for SportsPay */}
                  {paymentProvider === PAYMENT_PROVIDERS.SPORTS_PAY && (
                    <CreditCardInput
                      onChange={handleCreditCardChange}
                      companyName={billing?.organization_name || 'SportNinja'}
                      countries={countries}
                    />
                  )}

                  <div id='card-element' />
                  {/* Only show Stripe payment element if using Stripe */}
                  {paymentProvider === PAYMENT_PROVIDERS.STRIPE && (
                    <div
                      id='payment-element'
                      css={css`
                        min-height: 300px;
                      `}
                    />
                  )}
                  <button
                    disabled={paymentIsLoading}
                    id='submit'
                    css={css`
                      height: 40px;
                      display: flex;
                      padding: 8px 24px;
                      justify-content: center;
                      align-items: center;
                      gap: 8px;
                      flex: 1 0 0;
                      border-radius: 8px;
                      cursor: ${paymentIsLoading ? 'not-allowed' : 'pointer'};
                      background-color: ${colors.PRIMARY};
                      color: ${colors.WHITE};
                      text-transform: uppercase;
                      margin-top: 16px;
                      width: 100%;
                      ${paymentIsLoading &&
                      css`
                        background-color: ${colors.SECONDARY_200};
                      `}
                    `}
                  >
                    {paymentIsLoading ? <LoadingSpinner size={2} /> : 'PAY NOW'}
                  </button>
                </form>
              </div>
              {!bootstrapFinished && (
                <div
                  css={css`
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                  `}
                >
                  <LoadingSpinner />
                </div>
              )}
            </div>
          )}
        </div>
        {displayError && (
          <Alert
            message={displayError}
            type='error'
            showIcon
            css={css`
              margin-top: 16px;
              width: 100%;
            `}
          />
        )}
      </div>
    </div>
  )
}

export default RegistrationPayment
